// CartSection.tsx
import React from "react";
import {
  MdAddShoppingCart,
  MdPlaylistAdd,
  MdDelete,
  MdRemove,
  MdAdd,
  MdCheck,
} from "react-icons/md";
import { useTheme } from "../../hooks/useTheme";
import styles from "./CartSection.module.scss";
import { unitTypeToText } from "../../utils/functions/enumToText";
import { formatPriceToLocale } from "../../utils/functions/formatPriceToLocale";

interface CartSectionProps {
  price?: number;
  unit?: number;
  quantity: number;
  onAdd: () => void;
  onIncrease: () => void;
  onDecrease: () => void;
  onRemove: () => void;
  layout?: "item" | "group";
  isOutOfStock?: boolean;
  maxAvailable?: number;
  isIncreaseDisabled?: boolean;
  isComplexCategory?: boolean;
  showComplexButton?: boolean;
  showCartButton?: boolean;
  onAddToComplex?: () => void;
  isInComplexCart?: boolean;
}

export const CartSection: React.FC<CartSectionProps> = ({
  price,
  unit,
  quantity,
  onAdd,
  onIncrease,
  onDecrease,
  onRemove,
  layout = "item",
  isOutOfStock,
  isIncreaseDisabled,
  maxAvailable,
  showComplexButton,
  showCartButton = true,
  onAddToComplex,
  isInComplexCart = false,
}) => {
  const { theme } = useTheme();
  const hasPrice = Boolean(price);
  const unitText =
    unit !== undefined && unit !== -1 ? unitTypeToText(unit) : "";

  const renderQuantityControls = () => (
    <div className={styles.quantityContainer}>
      <button
        className={styles.quantityButton}
        onClick={quantity === 1 ? onRemove : onDecrease}
      >
        {quantity === 1 ? (
          <MdDelete size={20} color={theme.primary} />
        ) : (
          <MdRemove size={20} color={theme.primary} />
        )}
      </button>
      <div className={styles.quantityDisplay}>
        <span className={styles.quantityText}>{quantity}</span>
        <span className={styles.quantityUnit}>шт</span>
      </div>
      <button
        className={`${styles.quantityButton} ${isIncreaseDisabled ? styles.disabledButton : ""}`}
        onClick={onIncrease}
        disabled={isIncreaseDisabled}
      >
        <MdAdd
          size={20}
          color={
            isIncreaseDisabled ? "var(--text-secondary)" : "var(--primary)"
          }
        />
      </button>
    </div>
  );

  if (quantity > 0) {
    return renderQuantityControls();
  }

  // Одна большая кнопка "В проект" (для комплексных товаров без обычной корзины)
  if (showComplexButton && !showCartButton) {
    return (
      <button className={styles.complexButton} onClick={onAddToComplex}>
        {isInComplexCart ? (
          <MdCheck size={20} color="#fff" />
        ) : (
          <MdPlaylistAdd size={20} color="#fff" />
        )}
        <span className={styles.addButtonText}>
          {isInComplexCart ? "В проекте" : "В проект"}
        </span>
      </button>
    );
  }

  // Обычная категория: "В корзину" + маленькая "В проект"
  return (
    <section className={`${styles.cartSection} ${styles[layout]}`}>
      <div className={styles.cartRow}>
        <div className={styles.priceWrapper}>
          <span className={styles.cartPrice}>
            {hasPrice ? `${formatPriceToLocale(price)} ₽` : ""}
            {unitText && ` / ${unitText}`}
          </span>
          {maxAvailable !== undefined && maxAvailable < Infinity && (
            <span className={styles.maxAvailable}>{maxAvailable} шт</span>
          )}
        </div>
        {maxAvailable !== undefined && maxAvailable < Infinity && (
          <span className={styles.maxAvailable}>{maxAvailable} шт</span>
        )}
      </div>

      <div className={styles.buttonsRow}>
        {showCartButton && (
          <button
            className={`${styles.addButton} ${!hasPrice || isOutOfStock ? styles.addButtonDisabled : ""}`}
            onClick={onAdd}
            disabled={!hasPrice || isOutOfStock}
          >
            <MdAddShoppingCart size={20} color="#fff" />
            <span className={styles.addButtonText}>В корзину</span>
          </button>
        )}
        {showComplexButton && (
          <button
            className={styles.complexButtonSmall}
            onClick={onAddToComplex}
          >
            {isInComplexCart ? (
              <MdCheck size={20} color="var(--primary)" />
            ) : (
              <MdPlaylistAdd size={20} color="var(--primary)" />
            )}
          </button>
        )}
      </div>
    </section>
  );
};
